<template>
    <div>
        <div class="upload-header py-1 d-flex">
            <h4 class="d-inline me-auto">{{ title }}</h4>
            <div class="upload-document" v-if="fileDetails.length <= 0 && addPatientDocumentsPermission">
                <button type="button" class="btn btn-primary border border-primary">Add Documents</button>
                <input type="file" @change="uploadFilesChanged($event)" class="file-input" multiple />
            </div>
        </div>
        <div class="upload-filters">
            <slot name="document-filters"></slot>
        </div>
        <div class="upload-body">
            <div class="new-documents pb-4" v-if="fileDetails.length > 0">
                <div v-if="!uploading">
                    <table class="table table-responsive-md borderless">
                        <thead>
                            <tr class="bg-table-header">
                                <th></th>
                                <th>Name</th>
                                <th>Type <span class="text-danger">*</span></th>
                                <th class="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody class="hh-document-table" v-for="(fileDetail, index) in fileDetails" :key="index">
                            <tr>
                                <td>
                                    <i
                                        :class="`file-icon far fa-file ${getFileIcon(fileDetail.type)}`"
                                        v-if="fileDetail.type"
                                    ></i>
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        placeholder="Enter Name"
                                        class="form-control"
                                        :value="fileDetail.file.name"
                                        :disabled="uploading"
                                    />
                                </td>
                                <td class="form-group">
                                    <div>
                                        <TypeaheadInput
                                            placeholder="Start Typing..."
                                            :data="searchedTypes"
                                            :value="selectedUploadType"
                                            v-on:valueChanged="(newValue:any) => updateTypeId(index, newValue.value)"
                                            v-on:blurredInput="v$.fileDetails[index].uploadTypeId.$touch()"
                                            :disabled="uploading"
                                            :showAllByDefault="true"
                                            :openOnFocus="true"
                                        ></TypeaheadInput>
                                        <span v-if="isFieldRequired(index)" class="field-invalid"
                                            >Type is required.</span
                                        >
                                    </div>
                                </td>
                                <td class="text-end align-middle">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-danger"
                                        @click="removeFile(index)"
                                    >
                                        <i class="fas fa-trash-alt"></i> Delete
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <footer class="upload-footer pt-2 pb-3 d-flex align-items-center justify-content-end">
                        <span class="more-files-wrapper me-2">
                            <a href="javascript:void(0);">Add Another Document</a>
                            <input
                                type="file"
                                class="file-input"
                                multiple
                                @change="uploadFilesChanged($event)"
                                :disabled="uploading"
                            />
                        </span>
                        <button
                            type="button"
                            class="btn btn-xs btn-primary me-2"
                            @click="uploadDocuments()"
                            :disabled="uploading"
                        >
                            Save Documents
                        </button>
                        <button
                            type="button"
                            class="btn btn-xs btn-outline-primary"
                            @click="removeAllFiles()"
                            :disabled="uploading"
                        >
                            Cancel
                        </button>
                    </footer>
                </div>
                <div class="mb-4" v-else>
                    <h5><i class="fa fa-spinner fa-spin"></i> Uploading</h5>
                </div>
            </div>
            <table class="table table-responsive-md borderless">
                <thead>
                    <tr class="bg-table-header">
                        <th>Name</th>
                        <th>Type</th>
                        <th>Last Modified</th>
                        <th class="text-end">Actions</th>
                    </tr>
                </thead>
                <tbody v-if="patientDocuments?.length == 0 && !loadingDocuments">
                    <TableEmpty :col-span="5" component="documents" secondary-message="No Documents Found" />
                </tbody>
                <tbody v-else>
                    <template v-if="loadingDocuments">
                        <tr v-for="i in loadingCount" :key="i">
                            <td><span class="shimmer-loading block-size-8"></span></td>
                            <td><span class="shimmer-loading block-size-6"></span></td>
                            <td><span class="shimmer-loading block-size-6"></span></td>
                            <td><span class="shimmer-loading block-size-4"></span></td>
                        </tr>
                    </template>

                    <template v-if="!loadingDocuments">
                        <tr v-for="document in patientDocuments" :key="document.Id">
                            <td>
                                <i class="far fa-file me-2 fa-lg"></i>
                                <a href="javascript:void(0);" @click="downloadDocuments(document)">{{
                                    document.DocumentName
                                }}</a>
                            </td>
                            <td>{{ document?.DocumentTypeName }}</td>
                            <td>{{ formatDate(document.ModifiedDateFormatted) }}</td>
                            <td class="text-end">
                                <template v-if="editPatientDocumentsPermission || deletePatientDocumentsPermission">
                                    <button
                                        type="button"
                                        class="btn table-btn btn-outline-info au-target"
                                        @click="editSelectedDocument(document)"
                                        v-tooltip="{ title: 'Edit' }"
                                        v-if="
                                            editPatientDocumentsPermission && !document.hasOwnProperty('IsPatientChart')
                                        "
                                    >
                                        <i class="fas fa-edit"></i>
                                    </button>
                                    <button
                                        type="button"
                                        class="btn table-btn btn-outline-danger"
                                        @click="deleteSelectedDocument(document?.Id)"
                                        v-tooltip="{ title: 'Delete' }"
                                        v-if="
                                            deletePatientDocumentsPermission &&
                                            !document.hasOwnProperty('IsPatientChart')
                                        "
                                    >
                                        <i class="fas fa-trash-alt"></i>
                                    </button>
                                    <button
                                        type="button"
                                        class="btn table-btn btn-outline-info au-target"
                                        @click="openSelectedDocument(document)"
                                        v-tooltip="{ title: 'View' }"
                                        v-if="document.IsPatientChart && document.Status != 'Running' && document.Status != 4"
                                    >
                                        <i class="fa fa-eye"></i>
                                    </button>
                                </template>
                                <i class="text-default" v-else>No Actions</i>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script lang="ts" src="./HHPatientDocuments.ts" />
<style lang="scss" src="./HHPatientDocuments.scss"></style>
